'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowRight } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import Autoplay from 'embla-carousel-autoplay';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

interface ToolCard {
  id: string;
  title: string;
  description: string;
  image: string;
  link: string;
  linkText: string;
}

const defaultTools: ToolCard[] = [
  {
    id: 'textRemover',
    title: 'Remove text from image',
    description:
      'Remove unwanted text from images while ensuring a seamless result with AI-powered precision.',
    image:
      'https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/a23dfe5e-91ac-4b55-378a-e67b13069b00/public',
    link: '/remove-text-from-image',
    linkText: 'Start free trial',
  },
  {
    id: 'unblurImage',
    title: 'Unblur image',
    description:
      "Fix blurry images instantly with Kaze AI's AI-powered enhancer and restore clarity in seconds, no sign-up needed.",
    image:
      'https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/7aab9251-0ae8-4525-c536-53d98daeaf00/public',
    link: '/unblur-image',
    linkText: 'Start free trial',
  },
  {
    id: 'aiHeadshot',
    title: 'AI headshot generator',
    description:
      'Create professional headshots in minutes. Perfect for LinkedIn, resumes, or personal branding.',
    image:
      'https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/91876825-25dd-4876-7ab7-c14c37610900/public',
    link: '/ai-headshot-generator',
    linkText: 'Get AI headshot',
  },
  {
    id: 'stickerRemover',
    title: 'Sticker remover',
    description:
      'Our AI-powered sticker remover cleans up your image in just a few clicks, delivering a high-quality result.',
    image:
      'https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/88853d76-74c7-47c6-7328-9152502cfa00/public',
    link: '/sticker-remover',
    linkText: 'Start free trial',
  },
  {
    id: 'logoRemover',
    title: 'Logo remover',
    description:
      'Your AI-powered tool removes logos instantly with one click, delivering a clean, high-quality result in seconds.',
    image:
      'https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/e50ad91c-25d3-4a01-e36e-8dafc1db2900/public',
    link: '/logo-remover',
    linkText: 'Start free trial',
  },
  {
    id: 'imageUpscaler',
    title: 'Image upscaler',
    description:
      'Instantly enhances low-resolution images, preserving sharp details for professional results with one click.',
    image:
      'https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/73e9ede7-7741-495b-875d-f0ecd2f6a900/public',
    link: '/image-upscaler',
    linkText: 'Start free trial',
  },
];

// 定义链接按钮的动画变体
const linkButtonVariants = {
  rest: {
    x: 0,
    transition: { duration: 0.2, ease: 'easeInOut' },
  },
  hover: {
    x: 5,
    transition: { duration: 0.2, ease: 'easeInOut' },
  },
};

// 定义箭头的动画变体
const arrowVariants = {
  rest: { x: 0 },
  hover: {
    x: 5,
    transition: { type: 'spring', stiffness: 400, damping: 8 },
  },
};

interface CardItemProps {
  tool: ToolCard;
}

const CardItem = ({ tool }: CardItemProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link href={tool.link} className='block'>
      <motion.div
        className='mx-4 h-[480px] overflow-hidden rounded-xl border border-neutral-300/50 bg-white lg:h-fit 2xl:h-[700px]'
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        layout
      >
        <div className='h-[280px] w-full overflow-hidden 2xl:h-[500px]'>
          <motion.div
            animate={{
              scale: isHovered ? 1.02 : 1,
            }}
            transition={{
              duration: 0.4,
              ease: 'easeOut',
            }}
            className='relative h-full'
          >
            <Image
              src={tool.image}
              alt={tool.title}
              width={800}
              height={600}
              className='h-full w-full object-cover'
              priority
            />
          </motion.div>
        </div>
        <motion.div
          className='relative z-10 flex h-[200px] flex-col justify-between p-5 lg:h-[200px]'
          layout
        >
          <div className='min-h-0 flex-1'>
            <motion.h3
              className='mb-2 text-lg font-semibold md:text-xl lg:text-2xl'
              layout
            >
              {tool.title}
            </motion.h3>
            <motion.p
              className='overflow-hidden text-sm leading-relaxed text-[#12110C]/80 lg:text-sm xl:text-base 2xl:text-lg'
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}
              layout
            >
              {tool.description}
            </motion.p>
          </div>

          {/* 移动端始终显示的链接 */}
          <div className='mt-3 block flex-shrink-0 lg:hidden'>
            <div
              className='inline-flex items-center gap-2 text-base font-medium'
              onClick={(e) => e.stopPropagation()}
            >
              <span className='bg-gradient-to-r from-[#FD5C63] to-[#4C00FF] bg-clip-text text-transparent hover:opacity-90'>
                {tool.linkText}
              </span>
              <span>
                <ArrowRight size={18} className='text-gray-700' />
              </span>
            </div>
          </div>

          {/* PC端悬停显示的链接 */}
          <AnimatePresence>
            {isHovered && (
              <motion.div
                initial={{ opacity: 0, height: 0, y: -10 }}
                animate={{
                  opacity: 1,
                  height: 'auto',
                  y: 0,
                  transition: {
                    opacity: { duration: 0.2 },
                    height: { duration: 0.2 },
                    y: { duration: 0.2, delay: 0.05 },
                  },
                }}
                exit={{
                  opacity: 0,
                  height: 0,
                  y: -10,
                  transition: {
                    opacity: { duration: 0.1 },
                    height: { duration: 0.2, delay: 0.05 },
                    y: { duration: 0.1 },
                  },
                }}
                className='hidden lg:block'
                layout
              >
                <motion.div
                  variants={linkButtonVariants}
                  initial='rest'
                  whileHover='hover'
                >
                  <div
                    className='inline-flex items-center gap-2 text-lg font-medium'
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className='bg-gradient-to-r from-[#FD5C63] to-[#4C00FF] bg-clip-text text-transparent hover:opacity-90'>
                      {tool.linkText}
                    </span>
                    <motion.span variants={arrowVariants}>
                      <ArrowRight size={20} className='text-gray-700' />
                    </motion.span>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </Link>
  );
};

export interface ToolCardsProps {
  translations?: {
    namespace?: string;
  };
}

export function ToolCards({ translations }: ToolCardsProps = {}) {
  // 使用翻译
  const t = useTranslations(translations?.namespace || 'ToolCards');

  // 处理翻译后的工具卡片数据
  const tools = defaultTools.map((tool) => ({
    ...tool,
    title: t(`${tool.id}.title`, { defaultValue: tool.title }),
    description: t(`${tool.id}.description`, {
      defaultValue: tool.description,
    }),
    linkText: t(`${tool.id}.linkText`, { defaultValue: tool.linkText }),
  }));

  // 自动播放插件
  const plugin = useMemo(
    () =>
      Autoplay({
        delay: 5000,
      }),
    []
  );

  return (
    <div className='w-full px-5 pb-5 lg:px-28'>
      {/* 移动端轮播 */}
      <div className='block lg:hidden'>
        <Carousel
          opts={{
            align: 'center',
            loop: true,
          }}
          plugins={[plugin]}
          className='w-full'
        >
          <CarouselContent className='-ml-5'>
            {tools.map((tool, index) => (
              <CarouselItem key={index} className='basis-full pl-5'>
                <CardItem tool={tool} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className='absolute -left-2 top-1/2 z-10 hidden -translate-y-1/2 md:block'>
            <CarouselPrevious className='h-10 w-10' />
          </div>
          <div className='absolute -right-2 top-1/2 z-10 hidden -translate-y-1/2 md:block'>
            <CarouselNext className='h-10 w-10' />
          </div>
        </Carousel>
      </div>

      {/* 桌面端轮播 */}
      <div className='hidden lg:block'>
        <Carousel
          opts={{
            align: 'center',
            loop: true,
          }}
          plugins={[plugin]}
          className='w-full'
        >
          <CarouselContent className='-ml-5'>
            {tools.map((tool, index) => (
              <CarouselItem key={index} className='basis-1/3 pl-5'>
                <CardItem tool={tool} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className='absolute -left-6 top-1/2 z-10 hidden -translate-y-1/2 md:block'>
            <CarouselPrevious className='h-10 w-10' />
          </div>
          <div className='absolute -right-6 top-1/2 z-10 hidden -translate-y-1/2 md:block'>
            <CarouselNext className='h-10 w-10' />
          </div>
        </Carousel>
      </div>
    </div>
  );
}
